import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Check, IndianRupee, CreditCard, Loader2, RefreshCw, PlusCircle, Zap, Gift } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth, useUser } from '@clerk/clerk-react';
import { API_BASE, redeemCoupon } from '../lib/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useState } from 'react';

// Type definitions
interface PurchaseProductParams {
  productId: string;
  getToken: () => Promise<string | null>;
}

// Fetch subscription status
const fetchSubscriptionStatus = async (getToken: () => Promise<string | null>) => {
  const token = await getToken();
  const response = await fetch(`${API_BASE}/subscription-status`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error('Failed to fetch subscription status');
  }
  return response.json();
};

// Create Razorpay order
const createRazorpayOrder = async (productId: string, getToken: () => Promise<string | null>) => {
  const token = await getToken();
  const response = await fetch(`${API_BASE}/create-razorpay-order`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ productId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create order');
  }

  return response.json();
};

// Create Razorpay subscription
const createRazorpaySubscription = async (productId: string, getToken: () => Promise<string | null>) => {
  const token = await getToken();
  const response = await fetch(`${API_BASE}/create-razorpay-subscription`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ productId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create subscription');
  }

  return response.json();
};

// Verify Razorpay payment
const verifyRazorpayPayment = async (paymentData: any, getToken: () => Promise<string | null>) => {
  const token = await getToken();
  const response = await fetch(`${API_BASE}/verify-razorpay-payment`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(paymentData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Payment verification failed');
  }

  return response.json();
};

// Cancel subscription
const cancelSubscription = async (getToken: () => Promise<string | null>) => {
  const token = await getToken();
  const response = await fetch(`${API_BASE}/unsubscribe`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to cancel subscription');
  }

  return response.json();
};

export default function PaymentsPage() {
  const { getToken } = useAuth();
  const { user } = useUser();
  const queryClient = useQueryClient();
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const FEATURE_COUPONS = (import.meta as any).env?.VITE_FEATURE_COUPONS === 'true';

  // Redeem code state
  const [redeemCode, setRedeemCode] = useState('');
  const [redeemMsg, setRedeemMsg] = useState<string | null>(null);
  const [redeemStatus, setRedeemStatus] = useState<'idle' | 'checking' | 'success' | 'error'>('idle');

  const handleRedeem = async () => {
    const code = (redeemCode || '').trim();
    if (!code) return;
    setRedeemStatus('checking');
    setRedeemMsg(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      const resp = await redeemCoupon(code, token);
      if (resp?.success) {
        setRedeemStatus('success');
        setRedeemMsg(resp.label ? `Applied: ${resp.label}` : 'Code applied successfully');
        setRedeemCode('');
        // Refresh subscription status
        queryClient.invalidateQueries({ queryKey: ['subscriptionStatus'] });
      } else {
        setRedeemStatus('error');
        setRedeemMsg(resp?.reason ? `Failed: ${resp.reason}` : 'Failed to redeem code');
      }
    } catch (e: any) {
      setRedeemStatus('error');
      setRedeemMsg(e?.message || 'Failed to redeem code');
    }
  };

  const { data: status, isLoading: isStatusLoading, error: statusError, refetch } = useQuery({
    queryKey: ['subscriptionStatus'],
    queryFn: () => fetchSubscriptionStatus(getToken),
  });

  const purchaseMutation = useMutation({
    mutationFn: async (productId: string) => {
      // 1. Create order or subscription on backend
      let paymentData;
      const isSubscription = productId === 'monthly_subscription';

      if (isSubscription) {
        paymentData = await createRazorpaySubscription(productId, getToken);
      } else {
        paymentData = await createRazorpayOrder(productId, getToken);
      }

      // 2. Open Razorpay Checkout
      return new Promise((resolve, reject) => {
        const options: any = {
          key: paymentData.key,
          name: "HiPo AI Coach",
          description: isSubscription ? "Monthly Coaching Subscription" : `Top-up: ${productId.replace('_', ' ')}`,
          image: "https://hipoaicoach.com/hipo-logo.png",
          handler: async (response: any) => {
            try {
              // 3. Verify payment on backend
              const verificationResult = await verifyRazorpayPayment({
                ...response,
                productId
              }, getToken);
              resolve(verificationResult);
            } catch (err) {
              reject(err);
            }
          },
          prefill: {
            name: `${user?.firstName || ''} ${user?.lastName || ''}`,
            email: user?.primaryEmailAddress?.emailAddress || '',
          },
          theme: {
            color: "#3B82F6", // royal-blue
          },
          modal: {
            ondismiss: function () {
              reject(new Error('PAYMENT_CANCELLED'));
            }
          }
        };

        if (isSubscription) {
          options.subscription_id = paymentData.subscriptionId;
        } else {
          options.order_id = paymentData.orderId;
          options.amount = paymentData.amount;
          options.currency = paymentData.currency;
        }

        const rzp = new (window as any).Razorpay(options);
        rzp.on('payment.failed', function (response: any) {
          reject(new Error(response.error.description));
        });
        rzp.open();
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptionStatus'] });
      alert('Payment successful! Your subscription is now active.');
    },
    onError: (error) => {
      if (error.message === 'PAYMENT_CANCELLED') {
        // Silent failure for user cancellation to improve UX
        console.log('User cancelled the payment process');
        return;
      }
      alert(`Payment failed: ${error.message}`);
    },
  });

  const unsubscribeMutation = useMutation({
    mutationFn: () => cancelSubscription(getToken),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['subscriptionStatus'] });
      setIsCancelDialogOpen(false);
      alert(data.message || 'Subscription cancelled successfully.');
    },
    onError: (error: any) => {
      setIsCancelDialogOpen(false);
      alert(`Cancellation failed: ${error.message}`);
    },
  });

  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [isSummaryDialogOpen, setIsSummaryDialogOpen] = useState(false);

  const handlePurchaseClick = (productId: string) => {
    setSelectedProduct(productId);
    setIsSummaryDialogOpen(true);
  };

  const handleConfirmPurchase = () => {
    if (selectedProduct) {
      setIsSummaryDialogOpen(false);
      purchaseMutation.mutate(selectedProduct);
    }
  };

  const products = {
    monthly_subscription: {
      id: 'monthly_subscription',
      label: 'Monthly Subscription',
      priceInr: '₹99',
      cadence: '/month',
      features: [
        '7 coaching conversations per month',
        'Unused sessions carry forward (up to 3 months)',
        'Maximum 21 carry-forward sessions',
        'Minutes of Meeting (MoM) per conversation',
        'Access to Insights section',
      ],
    },
    top_up_pack: {
      id: 'top_up_pack',
      label: 'Top-Up Pack',
      priceInr: '₹40',
      cadence: 'one-time',
      features: ['3 additional coaching conversations'],
    },
  };

  return (
    <AppLayout currentPage="settings">
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-200">
          <div className="mx-auto max-w-7xl px-6 py-12 lg:px-8">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 rounded-full bg-royal-blue/10 px-4 py-2 text-sm font-medium text-royal-blue mb-6">
                <CreditCard className="h-4 w-4" />
                Subscription & Billing
              </div>
              <h1 className="font-display text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
                Simple, flexible pricing
              </h1>
              <p className="mt-6 text-lg leading-8 text-gray-600 max-w-2xl mx-auto">
                Choose a plan that works for you. Start with our monthly subscription and top-up whenever you need more sessions.
              </p>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-7xl px-6 py-12 lg:px-8">
          {/* Gift Subscription Promotion */}
          <div className="mb-12 bg-gradient-to-r from-royal-blue to-indigo-600 rounded-3xl p-8 text-white">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 p-3 rounded-2xl">
                  <Gift className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-display font-bold mb-1 !text-white">Gift a Coaching Journey</h3>
                  <p className="text-white">Give someone the gift of personal growth and AI-powered coaching insights</p>
                </div>
              </div>
              <Button
                onClick={() => window.location.href = '/gift'}
                className="bg-white !text-royal-blue hover:bg-white/90 font-semibold"
              >
                <Gift className="h-4 w-4 mr-2" />
                Start Gifting
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="border-royal-blue/30 ring-2 ring-royal-blue/20">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-display text-2xl font-bold">{products.monthly_subscription.label}</CardTitle>
                    <IndianRupee className="h-5 w-5 text-royal-blue" />
                  </div>
                  <div className="flex items-baseline gap-x-2">
                    <span className="text-4xl font-bold tracking-tight">{products.monthly_subscription.priceInr}</span>
                    <span className="text-sm font-semibold leading-6 tracking-wide text-gray-600">{products.monthly_subscription.cadence}</span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-3 text-sm">
                    {products.monthly_subscription.features.map(feature => (
                      <li key={feature} className="flex items-start gap-3">
                        <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter className="flex flex-col gap-3">
                  {status?.hasActiveSubscription ? (
                    <>
                      <Button className="w-full bg-emerald-100 text-emerald-700 hover:bg-emerald-100 cursor-default font-semibold border-emerald-200" disabled>
                        <Check className="h-4 w-4 mr-2" />
                        Subscription Active
                      </Button>
                      <Button
                        variant="ghost"
                        className="w-full text-red-500 hover:text-red-600 hover:bg-red-50 text-xs"
                        disabled={unsubscribeMutation.isPending}
                        onClick={() => setIsCancelDialogOpen(true)}
                      >
                        {unsubscribeMutation.isPending ? <><Loader2 className="h-3 w-3 animate-spin mr-2" />Cancelling...</> : 'Cancel Subscription'}
                      </Button>

                      <Dialog open={isCancelDialogOpen} onOpenChange={setIsCancelDialogOpen}>
                        <DialogContent className="sm:max-w-[425px]">
                          <DialogHeader>
                            <DialogTitle className="text-xl font-bold text-gray-900">Cancel Subscription?</DialogTitle>
                            <DialogDescription className="py-4 text-gray-600">
                              We're sorry to see you go. If you cancel now, you will still have access to your remaining sessions until the end of your current billing period.
                            </DialogDescription>
                          </DialogHeader>
                          <DialogFooter className="flex sm:justify-between gap-3 mt-4">
                            <Button
                              variant="outline"
                              onClick={() => setIsCancelDialogOpen(false)}
                              className="flex-1"
                            >
                              Keep Membership
                            </Button>
                            <Button
                              variant="destructive"
                              onClick={() => unsubscribeMutation.mutate()}
                              disabled={unsubscribeMutation.isPending}
                              className="flex-1 bg-red-600 hover:bg-red-700 !text-white"
                            >
                              {unsubscribeMutation.isPending ? (
                                <><Loader2 className="mr-2 h-4 w-4 animate-spin !text-white" /> Processing</>
                              ) : (
                                "Confirm Cancellation"
                              )}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </>
                  ) : (
                    <Button className="w-full bg-royal-blue !text-white hover:bg-royal-blue/90 disabled:text-white" disabled={purchaseMutation.isPending && selectedProduct === 'monthly_subscription'} onClick={() => handlePurchaseClick('monthly_subscription')}>
                      {purchaseMutation.isPending && selectedProduct === 'monthly_subscription' ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Processing...</> : 'Subscribe Now'}
                    </Button>
                  )}
                </CardFooter>
              </Card>

              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-display text-2xl font-bold">{products.top_up_pack.label}</CardTitle>
                    <PlusCircle className="h-5 w-5 text-gray-500" />
                  </div>
                  <div className="flex items-baseline gap-x-2">
                    <span className="text-4xl font-bold tracking-tight">{products.top_up_pack.priceInr}</span>
                    <span className="text-sm font-semibold leading-6 tracking-wide text-gray-600">{products.top_up_pack.cadence}</span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-3 text-sm">
                    {products.top_up_pack.features.map(feature => (
                      <li key={feature} className="flex items-start gap-3">
                        <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button className="w-full" variant="outline" disabled={purchaseMutation.isPending && selectedProduct === 'top_up_pack'} onClick={() => handlePurchaseClick('top_up_pack')}>
                    {purchaseMutation.isPending && selectedProduct === 'top_up_pack' ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Processing...</> : 'Purchase'}
                  </Button>
                </CardFooter>
              </Card>
            </div>

            <div className="lg:col-span-1 space-y-6">
              <Card className="sticky top-24">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Your Status</CardTitle>
                  <Button variant="ghost" size="icon" onClick={() => refetch()} disabled={isStatusLoading}>
                    <RefreshCw className={`h-4 w-4 ${isStatusLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </CardHeader>
                <CardContent>
                  {isStatusLoading ? (
                    <div className="flex items-center justify-center p-8"><Loader2 className="h-8 w-8 animate-spin" /></div>
                  ) : statusError ? (
                    <div className="text-red-500 text-sm">Failed to load status. Please try again.</div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Remaining Sessions</p>
                        <p className="text-2xl font-bold">{status?.sessionsRemaining ?? 0}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Current Plan</p>
                        <p className="font-semibold">{status?.tierConfig?.id.replace('_', ' ') || 'No active plan'}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {FEATURE_COUPONS && (
                <Card>
                  <CardHeader>
                    <CardTitle>Redeem a code</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <label htmlFor="redeem-code" className="block text-sm font-medium text-gray-700">Coupon code</label>
                      <div className="flex gap-2">
                        <input
                          id="redeem-code"
                          type="text"
                          value={redeemCode}
                          onChange={(e) => setRedeemCode(e.target.value)}
                          placeholder="Enter your code"
                          aria-label="Coupon code"
                          className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
                        />
                        <Button
                          type="button"
                          onClick={handleRedeem}
                          disabled={!redeemCode.trim() || redeemStatus === 'checking'}
                          className="whitespace-nowrap bg-royal-blue !text-white hover:bg-royal-blue/90"
                          title="Apply code"
                        >
                          {redeemStatus === 'checking' ? 'Applying…' : 'Apply'}
                        </Button>
                      </div>
                      {redeemMsg && (
                        <p className={`text-xs ${redeemStatus === 'success' ? 'text-green-700' : 'text-red-600'}`}>{redeemMsg}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={isSummaryDialogOpen} onOpenChange={(open) => {
        if (!open && !purchaseMutation.isPending) {
          setIsSummaryDialogOpen(false);
          setSelectedProduct(null);
        }
      }}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">Payment Summary</DialogTitle>
            <DialogDescription className="text-gray-600">
              Review your order details before proceeding to payment.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {selectedProduct === 'monthly_subscription' && (
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Monthly Subscription (Base)</span>
                  <span className="font-medium">₹99.00</span>
                </div>
                <div className="flex justify-between text-sm items-center">
                  <span className="text-gray-600">GST <span className="text-xs ml-1 bg-gray-100 px-1 py-0.5 rounded text-gray-500">18%</span></span>
                  <span className="font-medium">₹17.82</span>
                </div>
                <div className="border-t border-gray-200 pt-3 mt-4 flex justify-between font-bold text-lg">
                  <span className="text-gray-900">Total Amount</span>
                  <span className="text-royal-blue">₹116.82</span>
                </div>
              </div>
            )}
            {selectedProduct === 'top_up_pack' && (
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Top-Up Pack (Base)</span>
                  <span className="font-medium">₹40.00</span>
                </div>
                <div className="flex justify-between text-sm items-center">
                  <span className="text-gray-600">GST <span className="text-xs ml-1 bg-gray-100 px-1 py-0.5 rounded text-gray-500">18%</span></span>
                  <span className="font-medium">₹7.20</span>
                </div>
                <div className="border-t border-gray-200 pt-3 mt-4 flex justify-between font-bold text-lg">
                  <span className="text-gray-900">Total Amount</span>
                  <span className="text-royal-blue">₹47.20</span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="mt-6 flex gap-3 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setIsSummaryDialogOpen(false)}
              disabled={purchaseMutation.isPending}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmPurchase}
              disabled={purchaseMutation.isPending}
              className="w-full sm:w-auto bg-royal-blue !text-white hover:bg-royal-blue/90"
            >
              {purchaseMutation.isPending ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Processing...</> : 'Proceed to Pay'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
} 
